import { useEffect, useRef, type ReactNode } from 'react'
import { useOnline } from '../platform/network'

/**
 * Загрузка. Без сети загрузка не идёт (запрос ждёт сеть), и вместо мерцания
 * честно говорим, что на устройстве этого раздела нет. Это не «пусто» (данных
 * может быть много) и не ошибка сервера - третье состояние (ТЗ стр. 24).
 */
export function Skeleton({ rows = 2, label = 'Загружаю' }: { rows?: number; label?: string }) {
  const online = useOnline()
  if (!online) {
    return (
      <div className="pc-state" data-tone="offline" role="status">
        <h3>Нет сети, данных на устройстве нет</h3>
        <p>Этот раздел ещё не открывался с сетью. Он загрузится сам, когда сеть появится.</p>
      </div>
    )
  }
  return (
    <div role="status" aria-live="polite" style={{ marginTop: 16 }}>
      <span className="pc-visually-hidden">{label}</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="pc-skeleton" aria-hidden="true" />
      ))}
    </div>
  )
}

/** Пусто и ошибка выглядят по-разному: ошибку нельзя выдавать за пустой список. */
export function EmptyState({ title, text, action }: { title: string; text?: string; action?: ReactNode }) {
  return (
    <div className="pc-state">
      <h3>{title}</h3>
      {text && <p>{text}</p>}
      {action}
    </div>
  )
}

/**
 * Запись не прошла. Без сети так и говорим: «Нет сети - не сохранено» - и
 * ничего не копим на потом (ТЗ стр. 24), повтор только вручную.
 */
export function SaveFailedAlert({ children }: { children: ReactNode }) {
  const online = useOnline()
  return (
    <div className="pc-alert" role="alert">
      {online ? 'Не сохранилось.' : 'Нет сети - не сохранено.'} {children}
    </div>
  )
}

export function ErrorState({ title, text, onRetry }: { title: string; text?: string; onRetry: () => void }) {
  return (
    <div className="pc-state" data-tone="error" role="alert">
      <h3>{title}</h3>
      <p>{text ?? 'Проверьте связь и попробуйте ещё раз.'}</p>
      <button type="button" className="pc-btn pc-btn--primary" onClick={onRetry}>
        Повторить
      </button>
    </div>
  )
}

/** Нижняя шторка с выбором. Esc и тап по фону закрывают, фокус внутри. */
export function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null
    ref.current?.querySelector<HTMLElement>('button')?.focus()
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      prev?.focus()
    }
  }, [onClose])

  return (
    <div className="pc-sheet-backdrop" onClick={onClose}>
      <div
        className="pc-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={ref}
        onClick={(e) => e.stopPropagation()}
      >
        <h2>{title}</h2>
        {children}
      </div>
    </div>
  )
}
